import { useState } from 'react'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '~/shadcn/ui/carousel'
import { cn } from '~/shadcn/lib/utils'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import type { ProjectsItem } from '~/@pages_investing/projects/_project_types/project.types'
import { ProjectMedium } from '../project-media/ProjectMedium'

type Props = {
  projectId: string
  images: ProjectsItem['images']
  usage: 'Popup' | 'Details'
}

export function ProjectImageCarousel({ projectId, usage, images }: Props) {
  const [clickedVideos, setClickedVideos] = useState<Set<number>>(new Set())
  const [pausedVideos, setPausedVideos] = useState<Set<number>>(new Set())
  const isDesktop = useIsDesktop()

  const sortedImages = [...images].sort((a, b) => {
    const aIsVideo = a.path.endsWith('.mp4')
    const bIsVideo = b.path.endsWith('.mp4')
    if (aIsVideo && !bIsVideo) return -1
    if (!aIsVideo && bIsVideo) return 1
    return 0
  })

  const handleVideoClick = (videoElement: HTMLVideoElement, index: number) => {
    if (!clickedVideos.has(index)) {
      videoElement.currentTime = 0
      videoElement.muted = false
      videoElement.play()
      setClickedVideos((prev) => new Set([...prev, index]))
      setPausedVideos((prev) => {
        const next = new Set(prev)
        next.delete(index)
        return next
      })
    } else if (videoElement.paused) {
      videoElement.play()
      setPausedVideos((prev) => {
        const next = new Set(prev)
        next.delete(index)
        return next
      })
    } else {
      videoElement.pause()
      setPausedVideos((prev) => new Set(prev).add(index))
    }
  }

  if (!images?.length) return null

  const isPopup = usage === 'Popup'
  const showCarouselControls = isDesktop && sortedImages.length > 3

  return (
    <div
      className={cn(
        'relative w-full',
        isPopup ? 'h-full' : 'm-0 pt-10 pb-0 lg:pb-10',
        'bg-foreground'
      )}>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
        }}
        className="w-full h-full">
        <CarouselContent className="-ml-2 md:-ml-4 h-full">
          {sortedImages.map((img, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'relative h-[300px] lg:h-[350px] pl-2 md:pl-4',
                sortedImages.length === 1 ? 'basis-full' : 'basis-3/4',
                {
                  'lg:basis-full': sortedImages.length === 1,
                  'lg:basis-1/2':
                    sortedImages.length === 2 ||
                    (sortedImages.length >= 3 && index === 0),
                  'lg:basis-1/4': sortedImages.length === 3 && index !== 0,
                  'lg:basis-[22%]': sortedImages.length > 3 && index !== 0,
                }
              )}>
              <div className="relative w-full h-full">
                {img.path.endsWith('.mp4') ? (
                  <ProjectMedium projectId={projectId} filename={img.path}>
                    {(blob) => (
                      <>
                        <video
                          src={URL.createObjectURL(blob)}
                          autoPlay
                          muted
                          loop
                          playsInline
                          controls={false}
                          onClick={(e) =>
                            handleVideoClick(
                              e.target as HTMLVideoElement,
                              index
                            )
                          }
                          className="w-full h-full object-cover cursor-pointer rounded-lg"
                        />
                        {(!clickedVideos.has(index) ||
                          pausedVideos.has(index)) && (
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-transparent flex items-center justify-center pointer-events-none border-2 border-white">
                            <div className="w-0 h-0 border-l-[15px] border-l-white border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent ml-1" />
                          </div>
                        )}
                      </>
                    )}
                  </ProjectMedium>
                ) : (
                  <ProjectMedium
                    projectId={projectId}
                    filename={
                      img.sizes.find((size) => size.width === 770)?.path ??
                      img.path
                    }>
                    {(blob) => (
                      <img
                        src={URL.createObjectURL(blob)}
                        alt={`Slide ${index}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </ProjectMedium>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showCarouselControls && (
          <>
            <CarouselPrevious className="absolute left-[-50px] top-1/2 -translate-y-1/2" />
            <CarouselNext className="absolute right-[-50px] top-1/2 -translate-y-1/2" />
          </>
        )}
      </Carousel>
    </div>
  )
}
